// utils/filterStorage.ts (新設または既存の共通関数ファイルへ)
import dayjs from "dayjs";
import { ListingCategoryConst, PrefTypeConst } from '../constants'; // 合体セレクトボックス用の定数
import { isUnsetFilter, isValidCombination } from './filterValue';

const STORAGE_KEYS = {
  CATEGORY: "bidding_last_category",
  PREFECTURE: "bidding_last_prefecture",
  DATE: "bidding_filter_date"
};

/**
 * localStorage の値（文字列 or null）を数値に戻す。
 * 未保存・空文字は '' を返し、数値化できない値（"NaN" など）は NaN のまま返して
 * 呼び出し側の `isUnsetFilter` で弾けるようにする。
 */
const toStoredNumber = (raw: string | null): number | '' => {
  if (raw === null || raw === '') {
    return '';
  }
  return Number(raw);
};

export const saveFilterPreference = (category?: number, prefecture?: number) => {
  // 🔒 G2: 未設定（undefined/null/NaN）なら記憶をリセットする。
  //    `== null` だけでは NaN を素通りさせ、文字列 "NaN" が保存されてしまう
  //    （呼び出し側が Number("2026-09-15") のような変換をしたときに起きる＝G1 で塞いだ経路）。
  //    G1 が漏れても壊れたデータを残さないための二重化。
  //    ⚠ 県区分 0（入札会）は「未設定」ではないので、ここで消してはならない。
  if (isUnsetFilter(category) || isUnsetFilter(prefecture)) {
    localStorage.removeItem(STORAGE_KEYS.CATEGORY);
    localStorage.removeItem(STORAGE_KEYS.PREFECTURE);
    localStorage.removeItem(STORAGE_KEYS.DATE);
    return;
  }

  const today = dayjs().format("YYYYMMDD");
  localStorage.setItem(STORAGE_KEYS.CATEGORY, String(category));
  localStorage.setItem(STORAGE_KEYS.PREFECTURE, String(prefecture));
  localStorage.setItem(STORAGE_KEYS.DATE, today); 
};

export const getFilterPreference = () => {
  const savedDate = localStorage.getItem(STORAGE_KEYS.DATE);
  const today = dayjs().format("YYYYMMDD");

  // 保存された日付が今日と一致する場合のみ値を返す
  if (savedDate === today) {
    // 🔒 G3: 読み出し時にも未設定判定をかける。
    //    文字列 "NaN" は truthy なので `prefecture ? Number(prefecture) : 既定値` では
    //    素通りして NaN を返してしまい、受け取った画面が `?? 既定値` で拾えず壊れる
    //    （`??` は null/undefined しか拾わないため）。
    //    ⚠ **既に "NaN" が保存されている端末をここで自動回復させる**のが本ガードの主目的。
    //    ⚠ 文字列 "0" は truthy なので従来どおり 0 として読める（入札会の県区分）。
    const category = toStoredNumber(localStorage.getItem(STORAGE_KEYS.CATEGORY));
    const prefecture = toStoredNumber(localStorage.getItem(STORAGE_KEYS.PREFECTURE));
    const resolvedCategory = isUnsetFilter(category) ? ListingCategoryConst.BIDDING : (category as number);
    const resolvedPrefecture = isUnsetFilter(prefecture) ? PrefTypeConst.INTERNAL : (prefecture as number);

    // 🔒 K3（D-0086・タスク7）: 個々の未設定判定（上）だけでは「片方だけ有効値・組み合わせとしては不正」
    //    （例: 通常入札(2) × 県区分0）を拾えない。読み出し時に組み合わせ自体の正当性を確かめ、
    //    不正なら安全な既定値（通常入札・県内）へフォールバックする——localStorage を書いた側の
    //    バグ（過去の連携用CSV再出力のような固定値ミス）や複数タブでの書き込み競合から、
    //    読む側を独立して守るための防御（サーバの G8 を代替するものではない）。
    if (!isValidCombination(resolvedCategory, resolvedPrefecture)) {
      return { category: ListingCategoryConst.BIDDING, prefecture: PrefTypeConst.INTERNAL };
    }

    return { category: resolvedCategory, prefecture: resolvedPrefecture };
  }

  // 日付が違う（翌日になった）場合はクリアして初期値(通常県内)を返す
  localStorage.removeItem(STORAGE_KEYS.CATEGORY);
  localStorage.removeItem(STORAGE_KEYS.PREFECTURE);
  localStorage.removeItem(STORAGE_KEYS.DATE);
return {
    category: ListingCategoryConst.BIDDING,
    prefecture: PrefTypeConst.INTERNAL,
};
};